import gql from 'graphql-tag';

const SAVE_TICKET = gql`
  mutation(
    $subject: String
    $client: ClientInput
    $requester: UserInput
    $priority: String
    $impact: String
    $urgency: String
    $ticketType: String
    $status: String
    $priority: String
    $technician: UserInput
    $techGroup: GroupInput
    $sop: StandardOperatingProcedureInput
    $cause: String
    $subcause: String
    $category: String
    $subcategory: String
    $customFields: JSON
    $conversation: ConversationInput
    $updateWorklogTimer: UpdateWorklogTimerEntryInput
    $form: TicketFormIdentifierInput
    $addAssets: [AssetInput]
    $site: SiteInput
    $department: BusinessFunctionIdentifierInput
    $isMSPProduct: Boolean!
    $addAdditionalRequester: [UserInput]
  ) {
    saveTicket(
      input: {
        subject: $subject
        client: $client
        site: $site
        department: $department
        requester: $requester
        priority: $priority
        impact: $impact
        urgency: $urgency
        ticketType: $ticketType
        status: $status
        priority: $priority
        technician: $technician
        techGroup: $techGroup
        cause: $cause
        subcause: $subcause
        category: $category
        subcategory: $subcategory
        sop: $sop
        customFields: $customFields
        conversation: $conversation
        updateWorklogTimer: $updateWorklogTimer
        form: $form
        addAssets: $addAssets
        addAdditionalRequester: $addAdditionalRequester
      }
    ) {
      requester {
        userId
        firstName
        lastName
      }
      client @include(if: $isMSPProduct) {
        name
      }
      ticketId
      subject
      priority
      category
      createdTime
      status
      ticketType
      source
      displayId
      customFields
      sop {
        sopId
      }
      technician {
        userId
        firstName
        lastName
      }
      firstResponseDueTimeLeft
      resolutionDueTime
      resolutionDueTimeLeft
      firstResponseDueTime
    }
  }
`;

const UPDATE_TICKET_ASSETS = gql`
  mutation($id: ID!, $addAssets: [AssetInput], $deleteAssets: [AssetInput]) {
    updateTicket(
      id: $id
      input: { addAssets: $addAssets, deleteAssets: $deleteAssets }
    ) {
      ticketId
      firstResponseDueTime
      firstResponseDueTimeLeft
      firstResponseTime
      firstResponseViolated
      resolutionDueTime
      resolutionPauseTime
      resolutionDueTimeLeft
      resolutionTime
      status
      impact
      urgency
      priority
      category
      subcategory
      cause
      subcause
      resolutionCode
      techGroup {
        groupId
        name
      }
      followers {
        userId
        firstName
        lastName
        email
      }
      requester {
        userId
        firstName
        lastName
        email
      }
      technician {
        userId
        firstName
        lastName
        email
      }
      client {
        name
        accountId
      }
      additionalRequester {
        userId
        firstName
        lastName
        email
      }
      site {
        name
        id
      }
      displayId
      subject
      sop {
        sopId
        name
      }
      customFields
    }
  }
`;

const UPDATE_TICKET = gql`
  mutation(
    $id: ID!
    $subject: String
    $client: ClientInput
    $site: SiteInput
    $requester: UserInput
    $priority: String
    $impact: String
    $urgency: String
    $ticketType: String
    $status: String
    $cause: String
    $subcause: String
    $category: String
    $subcategory: String
    $resolutionCode: String
    $techGroup: GroupInput
    $customFields: JSON
    $technician: UserInput
    $suppressCloseNotification: Boolean
  ) {
    updateTicket(
      id: $id
      input: {
        subject: $subject
        client: $client
        site: $site
        requester: $requester
        priority: $priority
        impact: $impact
        urgency: $urgency
        ticketType: $ticketType
        status: $status
        cause: $cause
        subcause: $subcause
        category: $category
        subcategory: $subcategory
        resolutionCode: $resolutionCode
        techGroup: $techGroup
        customFields: $customFields
        technician: $technician
        suppressCloseNotification: $suppressCloseNotification
      }
    ) {
      ticketId
      firstResponseDueTime
      firstResponseDueTimeLeft
      firstResponseTime
      firstResponseViolated
      resolutionDueTime
      resolutionPauseTime
      resolutionDueTimeLeft
      resolutionTime
      status
      impact
      urgency
      priority
      category
      subcategory
      cause
      subcause
      resolutionCode
      techGroup {
        groupId
        name
      }
      followers {
        userId
        firstName
        lastName
        email
      }
      requester {
        userId
        firstName
        lastName
        email
      }
      technician {
        userId
        firstName
        lastName
        email
      }
      client {
        name
        accountId
      }
      additionalRequester {
        userId
        firstName
        lastName
        email
      }
      site {
        name
        id
      }
      displayId
      subject
      sop {
        sopId
        name
      }
      customFields
    }
  }
`;

const MERGE_TICKET = gql`
  mutation($input: MergeTicketsInput!) {
    combineTickets(input: $input)
  }
`;

const SOFT_TICKET_DELETE = gql`
  mutation($ids: [ID]!) {
    softDeleteTickets(ids: $ids)
  }
`;

const HARD_TICKET_DELETE = gql`
  mutation($ids: [ID]!) {
    hardDeleteTickets(ids: $ids)
  }
`;

const MARKSPAM_TICKETS = gql`
  mutation($ids: [ID]!, $markSpamRequester: Boolean) {
    markSpamTickets(ids: $ids, markSpamRequester: $markSpamRequester)
  }
`;

const MARK_UNSPAM_TICKETS = gql`
  mutation($input: UnspamTicketsInput!) {
    unspamTickets(input: $input)
  }
`;

const RESTORE_TICKETS = gql`
  mutation($ids: [ID]!) {
    restoreTickets(ids: $ids)
  }
`;

const SPLIT_TICKET = gql`
  mutation(
    $ticketId: ID!
    $noteId: ID
    $conversationId: ID
    $subject: String
    $copyFields: [String]
  ) {
    splitTicket(
      ticketId: $ticketId
      noteId: $noteId
      conversationId: $conversationId
      subject: $subject
      copyFields: $copyFields
    ) {
      ticketId
      firstResponseDueTime
      firstResponseDueTimeLeft
      firstResponseTime
      firstResponseViolated
      resolutionDueTime
      resolutionPauseTime
      resolutionDueTimeLeft
      resolutionTime
      status
      impact
      urgency
      priority
      category
      subcategory
      cause
      subcause
      resolutionCode
      techGroup {
        groupId
        name
      }
      technician {
        userId
        firstName
        lastName
        email
      }
      followers {
        userId
        firstName
        lastName
        email
      }
      requester {
        userId
        firstName
        lastName
        email
      }
      client {
        name
        accountId
      }
      additionalRequester {
        userId
        firstName
        lastName
        email
      }
      site {
        name
        id
      }
      displayId
      subject
      sop {
        sopId
        name
      }
    }
  }
`;

const UPDATE_TICKETS = gql`
  mutation($ticketInput: [TicketInput!]!) {
    updateTickets(input: $ticketInput) {
      ticketId
      displayId
      subject
      source
      assets {
        assetId
      }
      client {
        name
        accountId
      }
      site {
        name
        id
      }
    }
  }
`;

const CREATE_APPROVAL = gql`
  mutation($ticketId: ID!, $userIdList: [ID!]) {
    createApproval(ticketId: $ticketId, userIdList: $userIdList) {
      approvalId
      approver {
        userId
        firstName
        lastName
        name
      }
      status
      comment
      sentOn
      actionTakenOn
      ticket {
        ticketId
      }
      ticketPermission
    }
  }
`;

const UPDATE_APPROVAL = gql`
  mutation($approvalId: ID!, $approvalInput: ApprovalInput!) {
    updateApproval(approvalId: $approvalId, input: $approvalInput) {
      approvalId
      approver {
        userId
        firstName
        lastName
        name
      }
      status
      comment
      sentOn
      actionTakenOn
      ticket {
        ticketId
      }
      ticketPermission
    }
  }
`;

const RESEND_APPROVAL = gql`
  mutation($approvalId: ID!) {
    resendApproval(approvalId: $approvalId)
  }
`;

const CREATE_TICKET_SCHEDULE = gql`
  mutation($input: CreateTicketScheduleInput!) {
    createTicketSchedule(input: $input) {
      scheduleId
      name
      description
      schedule
      form {
        formId
      }
      data {
        subject
        description
        ticketType
      }
      nextRunAt
    }
  }
`;

const UPDATE_TICKET_SCHEDULE = gql`
  mutation($input: UpdateTicketScheduleInput!) {
    updateTicketSchedule(input: $input) {
      scheduleId
      name
      description
      schedule
      form {
        formId
      }
      data {
        subject
        description
        ticketType
      }
      nextRunAt
    }
  }
`;

const DELETE_TICKET_SCHEDULE = gql`
  mutation($input: TicketSchedulerIdentifierInput!) {
    deleteTicketSchedule(input: $input)
  }
`;

const DELETE_TICKET_SCHEDULES = gql`
  mutation($input: [TicketSchedulerIdentifierInput!]!) {
    deleteTicketSchedules(input: $input)
  }
`;

const RESEND_TICKET_CONVERSATION = gql`
  mutation($ticketId: ID!, $input: ConversationIdentifierInput!) {
    resendTicketConversation(ticketId: $ticketId, input: $input) {
      content
      time
      user {
        userId
        firstName
        lastName
        email
        name
      }
      outgoingMailStatus
      outgoingMailFailureReason
      conversationStatus
      failureReason
      recipientDetails {
        user {
          userId
          firstName
          lastName
          email
          name
        }
        label
        conversationRecipientType
      }
      conversationScope
      attachments {
        fileName
        originalFileName
        fileSize
      }
      type
      conversationId
      toUsers {
        user {
          userId
          firstName
          lastName
          name
          email
        }
        mailDeliveryStatus
        mailFailureReason
      }
      ccUsers {
        user {
          userId
          firstName
          lastName
          name
          email
        }
        mailDeliveryStatus
        mailFailureReason
      }
      bccUsers {
        user {
          userId
          firstName
          lastName
          name
          email
        }
        mailDeliveryStatus
        mailFailureReason
      }
    }
  }
`;
const RESEND_TICKET_INTEGRATION_CONVERSATION = gql`
  mutation($ticketId: ID!, $input: ConversationIdentifierInput!) {
    resendTicketConversation(ticketId: $ticketId, input: $input) {
      content
      time
      attachments {
        fileName
        originalFileName
        fileSize
      }
      type
      conversationId
      conversationStatus
      failureReason
      recipientDetails {
        user {
          userId
          name
          email
        }
        label
        conversationRecipientType
      }
      conversationScope
    }
  }
`;

const SEND_APPROVAL = gql`
  mutation sendApproval($input: SendApprovalInput!) {
    sendApproval(input: $input) {
      approvalId
      approver {
        userId
        name
      }
      status
    }
  }
`;

const DETACH_TICKETS = gql`
  mutation detachTickets($input: DetachTicketsInput!) {
    detachTickets(input: $input)
  }
`;

export {
  UPDATE_TICKET,
  UPDATE_TICKETS,
  SAVE_TICKET,
  MERGE_TICKET,
  SOFT_TICKET_DELETE,
  HARD_TICKET_DELETE,
  MARKSPAM_TICKETS,
  SPLIT_TICKET,
  CREATE_APPROVAL,
  UPDATE_APPROVAL,
  RESEND_APPROVAL,
  UPDATE_TICKET_ASSETS,
  RESTORE_TICKETS,
  CREATE_TICKET_SCHEDULE,
  UPDATE_TICKET_SCHEDULE,
  DELETE_TICKET_SCHEDULE,
  DELETE_TICKET_SCHEDULES,
  RESEND_TICKET_CONVERSATION,
  RESEND_TICKET_INTEGRATION_CONVERSATION,
  SEND_APPROVAL,
  MARK_UNSPAM_TICKETS,
  DETACH_TICKETS
};
